import { useCallback, useMemo } from 'react';

import {
  INVESTMENT_OPPORTUNITIES_EVENTS,
  INVESTMENT_OPPORTUNITIES_PLATFORM,
  INVESTMENT_OPPORTUNITIES_SOURCES,
  TAssetType,
  TTrack,
  useAnalytics,
} from '@r-client/shared/data/analytics';

/**
 * Analytics for the investment opportunities block on the homepage.
 *
 * The event *names* are the ones /investment-opportunities uses — it is the same
 * rail answering the same question, so splitting them would make the two
 * surfaces incomparable in the funnel. `source` is what separates them.
 *
 * A hook of its own rather than a parameter on
 * `useInvestmentOpportunitiesAnalytics`: the domain hook belongs to the feature
 * that owns the surface (the same split as `useFundedVenturesAnalytics`), and
 * reaching into that feature is what this whole block is built to avoid.
 *
 * No `pageViewed` — the homepage reports its own page view, with its region, in
 * `apps/republic/pages/index.page.tsx`.
 */
export function useHomepageOpportunitiesAnalytics() {
  const analytics = useAnalytics();

  const trackEvent = useCallback(
    (event: TTrack) => {
      analytics.track(event);
    },
    [analytics]
  );

  return useMemo(() => {
    const meta = {
      source: INVESTMENT_OPPORTUNITIES_SOURCES.HOMEPAGE,
      platform: INVESTMENT_OPPORTUNITIES_PLATFORM,
    } as const;

    return {
      assetCardClicked: (assetType: TAssetType, slug: string) =>
        trackEvent({
          name: INVESTMENT_OPPORTUNITIES_EVENTS.ASSET_CARD_CLICKED,
          params: { asset_type: assetType, slug, ...meta },
        }),

      seeAllClicked: (assetType: TAssetType) =>
        trackEvent({
          name: INVESTMENT_OPPORTUNITIES_EVENTS.SEE_ALL_CLICKED,
          params: { asset_type: assetType, ...meta },
        }),

      assetFollowed: (assetType: TAssetType, slug: string) =>
        trackEvent({
          name: INVESTMENT_OPPORTUNITIES_EVENTS.ASSET_FOLLOWED,
          params: { asset_type: assetType, slug, ...meta },
        }),

      assetUnfollowed: (assetType: TAssetType, slug: string) =>
        trackEvent({
          name: INVESTMENT_OPPORTUNITIES_EVENTS.ASSET_UNFOLLOWED,
          params: { asset_type: assetType, slug, ...meta },
        }),
    };
  }, [trackEvent]);
}
